import React, {
  createContext,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import {Platform} from 'react-native';
import {
  NavigationContainer,
  useNavigation,
  useNavigationContainerRef,
  useNavigationState,
} from '@react-navigation/native';
import {defer} from 'lodash';

import {LoadingView} from '/component/base/LoadingView';
import {
  getActiveRoutes as getActiveNavigationRoutes,
  readState as readNavigationState,
  writeState as writeNavigationState,
} from '/util/navigation.util';

interface NavigationContextType {
  navigateBack: (...args: any[]) => void;
  navigateHasBack: (...args: any[]) => boolean;
}

interface NavigationProviderProps {
  children: ReactNode;
  onRouteChange: (arg: {activeRoutes: string[]}) => void;
}

export const NavigationContext = createContext<NavigationContextType>({
  navigateBack: () => {},
  navigateHasBack: () => false,
});

// Create a global navigation ref that can be accessed from anywhere
export const navigationRef = React.createRef<any>();
// import {createNavigationContainerRef} from '@react-navigation/native';

// export const navigationRef = createNavigationContainerRef();
// Navigation methods that work on both platforms
const NavigationMethods: React.FC<{
  children: ReactNode;
  onRouteChange: (arg: {activeRoutes: string[]}) => void;
}> = ({children, onRouteChange}) => {
  const navigation = useNavigation();
  const activeRoutesNames = useNavigationState(state =>
    state ? getActiveNavigationRoutes(state).map(r => r.name) : [],
  );
  const [navigationStateReady, setNavigationStateReady] = useState(false);

  const isOnDashboard = activeRoutesNames.some(
    name => name === 'dashboard' || name === 'nextTask',
  );
  const canGoBack = navigation.canGoBack();

  // Use refs to avoid stale closures in the popstate listener
  const isOnDashboardRef = useRef(isOnDashboard);
  const canGoBackRef = useRef(canGoBack);
  const isExitingRef = useRef(false);

  useEffect(() => {
    isOnDashboardRef.current = isOnDashboard;
    canGoBackRef.current = canGoBack;
    // Reset exiting flag when navigating internally
    isExitingRef.current = false;
  }, [isOnDashboard, canGoBack]);

  // Web only: handle dashboard back button confirmation
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    // Trap the back button when on the root Dashboard
    if (isOnDashboard && !canGoBack) {
      if (!window.history.state || !window.history.state.isDashboardTrap) {
        window.history.pushState(
          {...window.history.state, isDashboardTrap: true},
          '',
          window.location.href,
        );
      }
    }
  }, [isOnDashboard, canGoBack]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handlePopState = (event: PopStateEvent) => {
      // If we land on a state without our flag, AND we were on the root dashboard
      if (isOnDashboardRef.current && !canGoBackRef.current) {
        if (!event.state || !event.state.isDashboardTrap) {
          const confirmed = window.confirm('Are you sure you want to leave?');
          if (confirmed) {
            // User confirmed. Set flag to skip beforeunload prompt.
            isExitingRef.current = true;
            // Browser has already moved back one step.
            // We move back one more step to actually leave the Dashboard entry.
            window.history.back();
          } else {
            // User cancelled. Push the trap state back to stay on the Dashboard.
            window.history.pushState(
              {...window.history.state, isDashboardTrap: true},
              '',
              window.location.href,
            );
          }
        }
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isOnDashboardRef.current && !isExitingRef.current) {
        e.preventDefault();
        e.returnValue = '';
        return 'Are you sure you want to leave?';
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    // Restore navigation state from async-storage
    if (Platform.OS !== 'web') {
      readNavigationState().then(navigationState => {
        setNavigationStateReady(true);

        // Notify initial route
        defer(() => {
          const activeRoutes = getActiveNavigationRoutes(navigationState).map(
            route => route.name,
          );
          onRouteChange({
            activeRoutes,
          });
        });
      });
    } else {
      setNavigationStateReady(true);
    }

    // Listen for app state changes
    // const appStateListener = AppState.addEventListener(
    // 'change',
    // nextAppState => {
    // if (nextAppState === 'active') {
    //   setTimeout(() => setAppState(nextAppState), 500);
    // } else if (nextAppState === 'background') {
    //   setAppState(nextAppState);
    // } else {
    //   setAppState(nextAppState);
    // }
    // },
    // );

    // return () => {
    // appStateListener?.remove?.();
    // };
  }, [onRouteChange]);

  // Navigation methods that work with React Navigation hooks
  const navigateBack = () => {
    if (navigation && navigation.canGoBack()) {
      navigation.goBack();
    }
  };

  const navigateHasBack = () => {
    return navigation ? navigation.canGoBack() : false;
  };

  if (!navigationStateReady) {
    return <LoadingView />;
  }

  return (
    <NavigationContext.Provider
      value={{
        navigateBack,
        navigateHasBack,
      }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const NavigationProvider: React.FC<NavigationProviderProps> = ({
  children,
  onRouteChange,
}) => {
  const navigationRef = useNavigationContainerRef();

  // Handle navigation state changes
  const handleNavigationStateChange = (state: any) => {
    if (__DEV__ && Platform.OS !== 'web') {
      writeNavigationState(state);
    }

    const activeRoutes = getActiveNavigationRoutes(state).map(
      route => route.name,
    );
    onRouteChange({
      activeRoutes,
    });
  };

  if (Platform.OS === 'web') {
    // On web, NavigationContainer is provided at root level
    // Just provide navigation methods via context
    return (
      <NavigationMethods onRouteChange={onRouteChange}>
        {children}
      </NavigationMethods>
    );
  }

  // On native platforms, provide full NavigationContainer
  return (
    <NavigationContainer
      ref={navigationRef}
      onStateChange={handleNavigationStateChange}>
      <NavigationMethods onRouteChange={onRouteChange}>
        {children}
      </NavigationMethods>
    </NavigationContainer>
  );
};

export default NavigationProvider;
